// Admin → Promotions. One mechanism, three kinds (M7b). English chrome, LTR.
//
// Route path stays `/coupons` even though the page is titled Promotions —
// renaming breaks the sidebar entry and every bookmark for no gain.
//
// 🔴 THE FORM IS KIND-FIRST, not one form with conditionally-greyed fields.
// `couponCreateInput` does not merely validate per kind, it INVERTS a field:
// `code` is required for `coupon` and forbidden for the other two. A single
// input that must be filled in one mode and empty in another reads as a bug.
// The same goes for `expiresAt` — optional for a coupon, REQUIRED for both
// automatic kinds, because an unbounded automatic offer silently discounts
// every checkout forever.
//
// 🔴 The form can never submit a body the API refuses: `validate()` in
// ./-promotions mirrors every refinement, and the submit button is disabled
// until it passes. Server errors still render (role="alert") — the API stays
// the authority.
//
// 🔴 The old row rendered a fixed-amount discount by dividing by 100 or 1000
// inline, so KWD 1.200 showed as "1.2". Everything goes through <Money> and
// lib/money.ts now. The route's own CURRENCIES const was a duplicate of the
// one in lib/money.ts and is gone.

import { Button, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';

import {
  KIND_LABEL,
  PROMOTION_KINDS,
  buildCreateInput,
  emptyDraft,
  findOverlapping,
  isAutomatic,
  isCurrencyLimited,
  promotionLabel,
  validate,
  windowState,
  type PromotionDraft,
  type PromotionField,
} from './-promotions';

import {
  ConfirmDialog,
  DataTable,
  FilterBar,
  Icon,
  Money,
  PageHeader,
  StatusBadge,
  actionsColumn,
  useTableState,
  useToast,
  type Column,
} from '~/components';
import { createCoupon, deleteCoupon, listCoupons, setCouponStatus, type Coupon } from '~/lib/admin';
import { ApiError } from '~/lib/api';
import { listPlans } from '~/lib/billing';
import { CURRENCIES, type Currency } from '~/lib/money';

export const Route = createFileRoute('/_authed/coupons')({
  component: PromotionsPage,
});

const KIND_TONE = {
  coupon: 'info',
  offer: 'warning',
  plan_discount: 'neutral',
} as const;

function PromotionsPage() {
  const qc = useQueryClient();
  const table = useTableState({ filterKeys: ['kind', 'status'] });
  const coupons = useQuery({ queryKey: ['coupons'], queryFn: ({ signal }) => listCoupons(signal) });
  // Same query key as _authed.plans.tsx, so the picker shares that cache.
  const plans = useQuery({ queryKey: ['plans'], queryFn: ({ signal }) => listPlans(signal) });

  const [draft, setDraft] = useState<PromotionDraft>(() => emptyDraft('coupon'));
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Coupon | null>(null);
  const [pendingArchive, setPendingArchive] = useState<Coupon | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { toast } = useToast();

  const auto = isAutomatic(draft.kind);
  const result = validate(draft);
  // Field errors stay hidden until the operator has tried to submit once —
  // a form that turns red while you are still typing the first character is
  // hostile. The button is disabled throughout regardless.
  const fieldError = (f: PromotionField) => (showErrors ? result.errors[f] : undefined);

  function set<K extends keyof PromotionDraft>(key: K, value: PromotionDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  // Switching kind keeps the discount, window, targeting and caps — all four
  // are meaningful in every kind — and clears `code`, which is the one field
  // the automatic kinds cannot carry at all. buildCreateInput strips it too,
  // so a leftover code can never reach the API; clearing it here is what keeps
  // the visible state honest.
  function setKind(kind: PromotionDraft['kind']) {
    setShowErrors(false);
    setError(null);
    setDraft((d) => ({ ...d, kind, code: kind === 'coupon' ? d.code : '' }));
  }

  // 🔴 Switching mode CLEARS the other field rather than leaving it parked.
  // Both resolvers branch `if (percent) … else if (amount)`, so a body with
  // both would silently apply the percent and leave the amount as dead data.
  // The schema refuses both now; this makes it unreachable from the console.
  function setMode(mode: PromotionDraft['mode']) {
    setDraft((d) => ({
      ...d,
      mode,
      percent: mode === 'percent' ? d.percent : '',
      amount: mode === 'amount' ? d.amount : '',
    }));
  }

  const create = useMutation({
    mutationFn: () => createCoupon(buildCreateInput(draft)),
    onSuccess: () => {
      const kind = draft.kind;
      setDraft(emptyDraft(kind));
      setShowErrors(false);
      toast(`${KIND_LABEL[kind]} created`, { tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['coupons'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Something went wrong.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCoupon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  });

  // Archive is the safe counterpart to delete: the promotion stops applying at
  // the next checkout, but its redemption history survives. Restoring is the
  // same call with the other status, which is why one mutation serves both.
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Coupon['status'] }) =>
      setCouponStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  });

  const planSlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of plans.data ?? []) map.set(p.id, p.slug);
    return map;
  }, [plans.data]);

  // Advisory only. resolveAutomaticPromotion picks the SINGLE BEST discount;
  // every loser is inert, and nothing else in the console says so.
  const overlapping = useMemo(() => findOverlapping(coupons.data ?? []), [coupons.data]);

  const rows = useMemo(() => {
    let list = coupons.data ?? [];
    const kind = table.filters.kind;
    if (kind) list = list.filter((r) => r.kind === kind);
    const status = table.filters.status;
    if (status) list = list.filter((r) => r.status === status);
    const q = table.q.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (r) => (r.code ?? '').toLowerCase().includes(q) || (r.name ?? '').toLowerCase().includes(q),
    );
  }, [coupons.data, table.q, table.filters.kind, table.filters.status]);

  const columns: Column<Coupon>[] = [
    {
      key: 'kind',
      header: 'Kind',
      alwaysVisible: true,
      render: (c) => (
        <StatusBadge
          status={c.kind}
          label={KIND_LABEL[c.kind] ?? c.kind}
          tone={KIND_TONE[c.kind] ?? 'neutral'}
        />
      ),
    },
    {
      key: 'identity',
      header: 'Code / name',
      alwaysVisible: true,
      // A code-less automatic promotion has no code by design — the partial
      // index `coupons_code_uniq WHERE code IS NOT NULL` exists for exactly
      // that. Fall back to the name, never to a blank cell.
      render: (c) => (
        <div className="min-w-0">
          {c.code ? (
            <p className="truncate font-mono font-medium text-admin-900">{c.code}</p>
          ) : (
            <p className="truncate font-medium text-admin-900">{c.name ?? 'Untitled'}</p>
          )}
          {c.code && c.name && <p className="truncate text-xs text-admin-400">{c.name}</p>}
        </div>
      ),
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (c) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {c.discountPercent != null ? (
            // If a legacy row somehow carries both, show the percent: that is
            // what the engine will actually charge.
            <span className="font-medium text-admin-800">{c.discountPercent}%</span>
          ) : c.discountAmountMinor != null ? (
            <Money amountMinor={c.discountAmountMinor} currency={c.discountCurrency ?? 'QAR'} />
          ) : (
            <span className="text-admin-300">—</span>
          )}
          {isCurrencyLimited(c) && (
            <span
              className="rounded-md bg-warning-50 px-1.5 py-0.5 text-xs font-medium text-warning-700 ring-1 ring-inset ring-warning-500/20"
              title={`A fixed amount only applies to checkouts in ${c.discountCurrency}. Customers paying in another currency see no discount at all — there is no FX at checkout, so the promotion is skipped.`}
            >
              {c.discountCurrency} only
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'appliesTo',
      header: 'Applies to',
      hideBelow: 'lg',
      render: (c) => {
        const parts: string[] = [];
        if (c.appliesToPlanId) parts.push(planSlug.get(c.appliesToPlanId) ?? 'a plan');
        if (c.appliesToInterval) parts.push(c.appliesToInterval === 'year' ? 'Yearly' : 'Monthly');
        return parts.length === 0 ? (
          <span className="text-admin-400">Any</span>
        ) : (
          <span className="text-admin-600">{parts.join(' · ')}</span>
        );
      },
    },
    {
      key: 'window',
      header: 'Window',
      hideBelow: 'md',
      render: (c) => {
        const state = windowState(c);
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {state === 'always' ? (
              <span className="text-admin-300">Always</span>
            ) : (
              <span className="text-xs text-admin-500">
                {c.startsAt ? formatDate(c.startsAt) : 'Now'} →{' '}
                {c.expiresAt ? formatDate(c.expiresAt) : 'never'}
              </span>
            )}
            {state === 'scheduled' && (
              <StatusBadge status="scheduled" label="Scheduled" tone="info" />
            )}
            {state === 'ended' && <StatusBadge status="ended" label="Ended" tone="neutral" />}
            {overlapping.has(c.id) && (
              <span
                className="rounded-md bg-warning-50 px-1.5 py-0.5 text-xs font-medium text-warning-700 ring-1 ring-inset ring-warning-500/20"
                title="Other automatic promotions overlap this one. Only the largest discount applies — discounts never stack, so the rest are doing nothing at checkout."
              >
                Overlaps
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'redemptions',
      header: 'Redemptions',
      align: 'end',
      render: (c) => (
        <StatusBadge
          status="redemptions"
          label={`${c.redemptions}${c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ''}`}
          // Amber once the cap is reached: a spent coupon silently failing at
          // checkout is a support ticket waiting to happen.
          tone={
            c.maxRedemptions != null && c.redemptions >= c.maxRedemptions ? 'warning' : 'neutral'
          }
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'end',
      hideBelow: 'md',
      render: (c) => <StatusBadge status={c.status} dot />,
    },
    actionsColumn<Coupon>({
      label: (c) => `${KIND_LABEL[c.kind] ?? 'promotion'} ${promotionLabel(c)}`,
      // An automatic promotion has no code, so the primary becomes Copy id
      // rather than a greyed-out Copy code — a disabled Copy code would imply
      // a code is coming, and none ever will.
      primary: (c) =>
        c.code
          ? {
              id: 'copy-code',
              label: 'Copy code',
              icon: 'copy',
              onSelect: () => void navigator.clipboard?.writeText(c.code!),
            }
          : {
              id: 'copy-id',
              label: 'Copy id',
              icon: 'copy',
              onSelect: () => void navigator.clipboard?.writeText(c.id),
            },
      actions: (c) => [
        ...(c.code
          ? [
              {
                id: 'copy-id',
                label: 'Copy id',
                icon: 'copy' as const,
                onSelect: () => void navigator.clipboard?.writeText(c.id),
              },
            ]
          : []),
        {
          id: 'view-plan',
          label: 'View plan',
          icon: 'card',
          onSelect: () => window.location.assign('/plans'),
          disabled: c.appliesToPlanId == null,
          disabledReason: 'This promotion is not tied to a plan.',
        },
        // Archive sits ABOVE the divider — it is the reversible, everyday
        // action, and the one an operator almost always wants instead of
        // Delete. Restoring needs no confirmation: it cannot destroy anything.
        c.status === 'archived'
          ? {
              id: 'restore',
              label: 'Restore promotion',
              icon: 'undo' as const,
              onSelect: () => {
                setStatus.mutate(
                  { id: c.id, status: 'active' },
                  {
                    onSuccess: () =>
                      toast(`Promotion ${promotionLabel(c)} restored`, { tone: 'success' }),
                  },
                );
              },
            }
          : {
              id: 'archive',
              label: 'Archive promotion',
              icon: 'inbox' as const,
              onSelect: () => setPendingArchive(c),
            },
        {
          id: 'delete',
          label: 'Delete promotion',
          icon: 'trash',
          destructive: true,
          onSelect: () => setPendingDelete(c),
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Promotions"
        description="Coupon codes customers type, and offers that apply themselves at checkout."
      />

      <form
        ref={formRef}
        className="mb-6 rounded-xl border border-admin-200 bg-white p-5"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setShowErrors(true);
          if (result.ok) create.mutate();
        }}
      >
        {/* Kind first: the fields below are the fields this kind actually has. */}
        <div className="mb-4">
          <p className="mb-2 text-sm font-medium text-ink-700">What are you creating?</p>
          <div className="flex flex-wrap gap-2">
            {PROMOTION_KINDS.map((k) => (
              <Tab key={k} active={draft.kind === k} onClick={() => setKind(k)}>
                {KIND_LABEL[k]}
              </Tab>
            ))}
          </div>
          <p className="mt-2 text-xs text-admin-500">{KIND_HINT[draft.kind]}</p>
        </div>

        {error && (
          <p role="alert" className="mb-3 text-sm text-error-600">
            {error}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {draft.kind === 'coupon' && (
            <Field label="Code" error={fieldError('code')}>
              <Input
                value={draft.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                placeholder="WELCOME20"
                aria-invalid={fieldError('code') != null}
              />
            </Field>
          )}

          <Field
            label={auto ? 'Name' : 'Name (optional)'}
            error={fieldError('name')}
            hint={
              auto
                ? 'How this promotion appears in this list. It has no code to identify it by.'
                : undefined
            }
          >
            <Input
              value={draft.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder={auto ? 'Back to school' : 'Launch coupon'}
              aria-invalid={fieldError('name') != null}
            />
          </Field>

          {/* ── Discount: percent OR fixed amount, never both ── */}
          <div className="sm:col-span-2">
            <p className="mb-2 text-sm font-medium text-ink-700">Discount</p>
            <div className="mb-2 flex gap-2">
              <Tab active={draft.mode === 'percent'} onClick={() => setMode('percent')}>
                Percent
              </Tab>
              <Tab active={draft.mode === 'amount'} onClick={() => setMode('amount')}>
                Fixed amount
              </Tab>
            </div>
            {draft.mode === 'percent' ? (
              <>
                <Input
                  type="number"
                  value={draft.percent}
                  onChange={(e) => set('percent', e.target.value)}
                  placeholder="20 (%)"
                  className="max-w-xs"
                  aria-invalid={fieldError('percent') != null}
                />
                <FieldError error={fieldError('percent')} />
              </>
            ) : (
              <>
                <div className="flex gap-2">
                  <Input
                    value={draft.amount}
                    onChange={(e) => set('amount', e.target.value)}
                    placeholder="10.00"
                    className="max-w-xs"
                    inputMode="decimal"
                    aria-invalid={fieldError('amount') != null}
                  />
                  <select
                    value={draft.currency}
                    onChange={(e) => set('currency', e.target.value as Currency)}
                    aria-label="Currency"
                    className="h-11 rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <FieldError error={fieldError('amount')} />
                {/* 🔴 resolveAutomaticPromotion SKIPS a fixed amount whose
                    currency is not the checkout currency. Correct (no FX at
                    checkout) but invisible: the offer looks live while doing
                    nothing for most customers. Not blocked — a QAR-only offer
                    is legitimate — but never silent. */}
                {auto && (
                  <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700">
                    <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
                    <span>
                      A fixed amount only applies to checkouts in {draft.currency}. Customers paying
                      in another currency see no discount at all. Use a percent to cover every
                      currency.
                    </span>
                  </p>
                )}
              </>
            )}
          </div>

          {/* ── Window ── */}
          <Field label="Starts (optional)" error={fieldError('startsAt')}>
            <Input
              type="datetime-local"
              value={draft.startsAt}
              onChange={(e) => set('startsAt', e.target.value)}
              aria-invalid={fieldError('startsAt') != null}
            />
          </Field>
          <Field
            label={auto ? 'Ends' : 'Ends (optional)'}
            error={fieldError('expiresAt')}
            hint={
              auto
                ? 'Required — an automatic promotion with no end discounts every checkout forever.'
                : undefined
            }
          >
            <Input
              type="datetime-local"
              value={draft.expiresAt}
              onChange={(e) => set('expiresAt', e.target.value)}
              aria-invalid={fieldError('expiresAt') != null}
            />
          </Field>

          {/* ── Targeting ── */}
          <Field
            label={draft.kind === 'plan_discount' ? 'Plan' : 'Plan (optional)'}
            error={fieldError('appliesToPlanId')}
          >
            {plans.isError ? (
              // Never render an empty select on a failed query — that looks
              // exactly like "no plans exist".
              <div className="flex items-center gap-2 py-2">
                <span className="text-sm text-error-600">Could not load plans.</span>
                <button
                  type="button"
                  onClick={() => void plans.refetch()}
                  className="text-sm font-semibold text-brand-600 underline"
                >
                  Retry
                </button>
              </div>
            ) : (
              <select
                value={draft.appliesToPlanId}
                onChange={(e) => set('appliesToPlanId', e.target.value)}
                disabled={plans.isLoading}
                aria-invalid={fieldError('appliesToPlanId') != null}
                className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900 disabled:opacity-50"
              >
                {/* A plan_discount requires a plan (admin.ts:176), so it gets
                    no "Any plan" escape hatch. */}
                {draft.kind !== 'plan_discount' && <option value="">Any plan</option>}
                {draft.kind === 'plan_discount' && <option value="">Choose a plan…</option>}
                {(plans.data ?? []).map((p) => (
                  // slug, never nameKey — nameKey is a raw translation key.
                  <option key={p.id} value={p.id}>
                    {p.slug}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field
            label="Interval (optional)"
            hint={
              draft.kind === 'plan_discount'
                ? 'Plan + Yearly is how a yearly-subscription discount is expressed.'
                : undefined
            }
          >
            <select
              value={draft.appliesToInterval}
              onChange={(e) =>
                set('appliesToInterval', e.target.value as PromotionDraft['appliesToInterval'])
              }
              className="h-11 w-full rounded-xl border border-ink-500/20 bg-white px-3 text-ink-900"
            >
              <option value="">Any interval</option>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </Field>

          {/* ── Caps ── */}
          <Field label="Max redemptions (optional)" error={fieldError('maxRedemptions')}>
            <Input
              type="number"
              value={draft.maxRedemptions}
              onChange={(e) => set('maxRedemptions', e.target.value)}
              placeholder="unlimited"
              aria-invalid={fieldError('maxRedemptions') != null}
            />
          </Field>
          <Field label="Max per customer (optional)" error={fieldError('maxRedemptionsPerOwner')}>
            <Input
              type="number"
              value={draft.maxRedemptionsPerOwner}
              onChange={(e) => set('maxRedemptionsPerOwner', e.target.value)}
              placeholder="unlimited"
              aria-invalid={fieldError('maxRedemptionsPerOwner') != null}
            />
          </Field>
        </div>

        <div className="mt-4">
          <Button type="submit" disabled={!result.ok || create.isPending}>
            {create.isPending ? 'Creating…' : `Create ${KIND_LABEL[draft.kind].toLowerCase()}`}
          </Button>
        </div>
      </form>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        loading={coupons.isLoading}
        error={coupons.error}
        onRetry={() => void coupons.refetch()}
        columnVisibility
        caption="Promotions"
        skeletonRows={4}
        empty={{
          icon: 'tag',
          title: table.isFiltered ? 'No promotions match these filters' : 'No promotions yet',
          description: table.isFiltered
            ? 'Try clearing the filters to see everything.'
            : 'Create a coupon customers type, or an offer that applies itself at checkout.',
          action: table.isFiltered ? (
            <Button type="button" onClick={() => table.clearFilters()}>
              Clear filters
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => {
                formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                formRef.current?.querySelector('input')?.focus();
              }}
            >
              Create your first promotion
            </Button>
          ),
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search code or name…"
            filters={[
              {
                key: 'kind',
                label: 'Kind',
                value: table.filters.kind ?? '',
                options: PROMOTION_KINDS.map((k) => ({ value: k, label: KIND_LABEL[k] })),
              },
              {
                key: 'status',
                label: 'Status',
                value: table.filters.status ?? '',
                options: [
                  { value: 'active', label: 'Active' },
                  { value: 'archived', label: 'Archived' },
                ],
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
      />

      {/* Archive is REVERSIBLE, so it gets the primary tone and no typed
          confirmation — reserving red for Delete keeps red meaningful. The
          copy's job is to make the difference between the two obvious. */}
      <ConfirmDialog
        open={pendingArchive != null}
        onOpenChange={(open) => !open && setPendingArchive(null)}
        title="Archive this promotion"
        description={
          pendingArchive && (
            <>
              <span className={pendingArchive.code ? 'font-mono font-semibold' : 'font-semibold'}>
                {promotionLabel(pendingArchive)}
              </span>{' '}
              will stop applying at checkout from now on
              {pendingArchive.kind === 'coupon'
                ? ' — customers who type the code will be told it is no longer active'
                : ''}
              .{' '}
              {pendingArchive.redemptions > 0
                ? `Its ${pendingArchive.redemptions} recorded redemption${
                    pendingArchive.redemptions === 1 ? '' : 's'
                  } stay intact.`
                : 'Nothing has been redeemed against it.'}{' '}
              You can restore it at any time.
            </>
          )
        }
        confirmLabel="Archive promotion"
        tone="primary"
        onConfirm={async () => {
          if (!pendingArchive) return;
          const label = promotionLabel(pendingArchive);
          await setStatus.mutateAsync({ id: pendingArchive.id, status: 'archived' });
          toast(`Promotion ${label} archived`, { tone: 'success' });
          setPendingArchive(null);
        }}
      />

      <ConfirmDialog
        open={pendingDelete != null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete this promotion"
        description={
          pendingDelete && (
            <>
              <span className={pendingDelete.code ? 'font-mono font-semibold' : 'font-semibold'}>
                {promotionLabel(pendingDelete)}
              </span>{' '}
              will stop working immediately, and its redemption history is deleted with it
              (coupon_redemptions cascades)
              {pendingDelete.redemptions > 0
                ? ` — including its ${pendingDelete.redemptions} recorded redemption${
                    pendingDelete.redemptions === 1 ? '' : 's'
                  }`
                : ''}
              . This cannot be undone.
              {pendingDelete.redemptions > 0 && (
                <>
                  {' '}
                  <strong className="font-semibold text-admin-800">Archive it instead</strong> to
                  stop it applying while keeping that history.
                </>
              )}
            </>
          )
        }
        confirmLabel="Delete promotion"
        tone="danger"
        onConfirm={async () => {
          if (!pendingDelete) return;
          const label = promotionLabel(pendingDelete);
          await remove.mutateAsync(pendingDelete.id);
          toast(`Promotion ${label} deleted`, { tone: 'success' });
          setPendingDelete(null);
        }}
      />
    </div>
  );
}

const KIND_HINT: Record<PromotionDraft['kind'], string> = {
  coupon: 'The customer types a code at checkout. Needs a code; an end date is optional.',
  offer:
    'Applies itself to every checkout inside its window. No code, and an end date is required.',
  plan_discount:
    'Applies itself to one plan — add an interval to make it yearly-only. No code, and an end date is required.',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm font-medium text-ink-700">
      {label}
      {children}
      {hint && !error && (
        <span className="mt-1 block text-xs font-normal text-admin-500">{hint}</span>
      )}
      <FieldError error={error} />
    </label>
  );
}

function FieldError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <span role="alert" className="mt-1 block text-xs font-normal text-error-600">
      {error}
    </span>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-brand-500 text-white'
          : 'border border-admin-200 bg-white text-admin-700 hover:bg-admin-50'
      }`}
    >
      {children}
    </button>
  );
}
